import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { Save, ExternalLink, Copy, Check, Plus, Trash2, Palette, Search } from 'lucide-react';
import { ImageUploadButton } from '@/components/ImageUploadButton';

const PartnerMinisiteEditorPage = () => {
  const [ms, setMs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);

  const load = async () => {
    try {
      const r = await api.get('/partner/minisite');
      setMs(r.data);
    } catch (e) {
      toast.error('Erro ao carregar minisite');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const set = (patch) => setMs((prev) => ({ ...prev, ...patch }));

  const updateBenefit = (idx, patch) => {
    const arr = [...(ms.benefits || [])];
    arr[idx] = { ...arr[idx], ...patch };
    set({ benefits: arr });
  };
  const addBenefit = () => set({ benefits: [...(ms.benefits || []), { title: '', description: '' }] });
  const removeBenefit = (idx) => set({ benefits: (ms.benefits || []).filter((_, i) => i !== idx) });

  const updateTestimonial = (idx, patch) => {
    const arr = [...(ms.testimonials || [])];
    arr[idx] = { ...arr[idx], ...patch };
    set({ testimonials: arr });
  };
  const addTestimonial = () => set({ testimonials: [...(ms.testimonials || []), { name: '', business: '', city: '', text: '' }] });
  const removeTestimonial = (idx) => set({ testimonials: (ms.testimonials || []).filter((_, i) => i !== idx) });

  const salvar = async () => {
    setSaving(true);
    try {
      // Envia apenas os campos editáveis
      const payload = {
        title: ms.title, subtitle: ms.subtitle, description: ms.description,
        keywords: ms.keywords, cta_text: ms.cta_text, hero_headline: ms.hero_headline,
        hero_body: ms.hero_body, logo_url: ms.logo_url, hero_image_url: ms.hero_image_url,
        video_url: ms.video_url, primary_color: ms.primary_color,
        whatsapp_contact: ms.whatsapp_contact, published: ms.published,
        benefits: ms.benefits, testimonials: ms.testimonials,
      };
      const r = await api.put('/partner/minisite', payload);
      setMs(r.data);
      toast.success('Minisite atualizado!');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  const copyLink = () => {
    if (!ms?.minisite_url) return;
    navigator.clipboard.writeText(ms.minisite_url);
    setCopied(true);
    toast.success('Link copiado!');
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading || !ms) return <div className="p-8">Carregando editor...</div>;

  return (
    <div className="p-8 max-w-5xl mx-auto" data-testid="minisite-editor-page">
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <Link to="/parceiro" className="text-sm text-slate-500 hover:text-slate-700">← Voltar ao painel</Link>
          <h1 className="text-4xl font-bold mt-2" style={{ fontFamily: 'Manrope, sans-serif' }}>Meu Minisite</h1>
          <p className="text-slate-600 mt-1">Personalize sua página pública com SEO. Cada cliente que se cadastrar por aqui fica vinculado a você.</p>
        </div>
        <Button onClick={salvar} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="btn-save-minisite">
          <Save className="w-4 h-4 mr-2" />
          {saving ? 'Salvando...' : 'Salvar tudo'}
        </Button>
      </div>

      {/* Link e status */}
      <Card className="mb-6 border-emerald-200 bg-emerald-50">
        <CardHeader><CardTitle className="text-lg flex items-center gap-2"><ExternalLink className="w-5 h-5 text-emerald-600" /> Seu link público</CardTitle></CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <code className="flex-1 bg-white px-3 py-2 rounded border text-sm break-all" data-testid="minisite-url">{ms.minisite_url}</code>
            <Button size="sm" variant="outline" onClick={copyLink} data-testid="btn-copy-minisite">
              {copied ? <Check className="w-4 h-4 mr-1" /> : <Copy className="w-4 h-4 mr-1" />}
              {copied ? 'Copiado!' : 'Copiar'}
            </Button>
            <a href={ms.minisite_url} target="_blank" rel="noopener noreferrer">
              <Button size="sm" variant="outline" data-testid="btn-open-minisite"><ExternalLink className="w-4 h-4 mr-1" /> Ver ao vivo</Button>
            </a>
          </div>
          <div className="flex items-center gap-3 bg-white p-3 rounded border">
            <Switch checked={!!ms.published} onCheckedChange={(v) => set({ published: v })} data-testid="switch-published" />
            <div className="flex-1">
              <p className="font-medium text-slate-900">{ms.published ? 'Publicado' : 'Não publicado'}</p>
              <p className="text-xs text-slate-500">Quando desligado, a página retorna 404 para visitantes.</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* SEO */}
      <Card className="mb-6">
        <CardHeader><CardTitle className="flex items-center gap-2"><Search className="w-5 h-5 text-blue-600" /> SEO — Google e redes sociais</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Título (aparece no Google e na aba do navegador)</Label>
            <Input value={ms.title || ''} onChange={(e) => set({ title: e.target.value })} maxLength={70} data-testid="input-title" />
            <p className="text-xs text-slate-500 mt-1">{(ms.title || '').length}/70 — ideal até 60 caracteres.</p>
          </div>
          <div>
            <Label>Subtítulo</Label>
            <Input value={ms.subtitle || ''} onChange={(e) => set({ subtitle: e.target.value })} data-testid="input-subtitle" />
          </div>
          <div>
            <Label>Descrição (meta description)</Label>
            <Textarea rows={3} value={ms.description || ''} onChange={(e) => set({ description: e.target.value })} maxLength={200} data-testid="input-description" />
            <p className="text-xs text-slate-500 mt-1">{(ms.description || '').length}/200 — ideal até 155 caracteres.</p>
          </div>
          <div>
            <Label>Palavras-chave (separadas por vírgula)</Label>
            <Input value={ms.keywords || ''} onChange={(e) => set({ keywords: e.target.value })} placeholder="PDV, sistema fiscal, NFC-e, ..." data-testid="input-keywords" />
          </div>
        </CardContent>
      </Card>

      {/* Hero */}
      <Card className="mb-6">
        <CardHeader><CardTitle>Seção principal (Hero)</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Título grande (chamada principal)</Label>
            <Input value={ms.hero_headline || ''} onChange={(e) => set({ hero_headline: e.target.value })} data-testid="input-hero-headline" />
          </div>
          <div>
            <Label>Texto abaixo do título</Label>
            <Textarea rows={3} value={ms.hero_body || ''} onChange={(e) => set({ hero_body: e.target.value })} data-testid="input-hero-body" />
          </div>
          <div>
            <Label>Texto do botão de ação (CTA)</Label>
            <Input value={ms.cta_text || ''} onChange={(e) => set({ cta_text: e.target.value })} placeholder="Ex.: Começar teste grátis" data-testid="input-cta" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>URL do seu logo (opcional)</Label>
              <Input value={ms.logo_url || ''} onChange={(e) => set({ logo_url: e.target.value })} placeholder="https://..." data-testid="input-logo" />
            </div>
            <div>
              <Label>URL da imagem principal (opcional)</Label>
              <Input value={ms.hero_image_url || ''} onChange={(e) => set({ hero_image_url: e.target.value })} placeholder="https://..." data-testid="input-hero-image" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>URL do vídeo YouTube (embed)</Label>
              <Input value={ms.video_url || ''} onChange={(e) => set({ video_url: e.target.value })} placeholder="https://www.youtube.com/embed/XXXX" data-testid="input-video" />
              <p className="text-xs text-slate-500 mt-1">Use a URL de embed (formato /embed/ID).</p>
            </div>
            <div>
              <Label className="flex items-center gap-2"><Palette className="w-4 h-4" /> Cor primária</Label>
              <div className="flex gap-2">
                <Input type="color" value={ms.primary_color || '#059669'} onChange={(e) => set({ primary_color: e.target.value })} className="w-16 h-10 p-1" data-testid="input-color" />
                <Input value={ms.primary_color || '#059669'} onChange={(e) => set({ primary_color: e.target.value })} className="flex-1" />
              </div>
            </div>
          </div>
          <div>
            <Label>Seu WhatsApp para contato (com DDI+DDD)</Label>
            <Input value={ms.whatsapp_contact || ''} onChange={(e) => set({ whatsapp_contact: e.target.value })} placeholder="5511947289661" data-testid="input-whatsapp" />
          </div>
        </CardContent>
      </Card>

      {/* Benefícios */}
      <Card className="mb-6">
        <CardHeader className="flex-row items-center justify-between space-y-0">
          <CardTitle>Benefícios ({(ms.benefits || []).length})</CardTitle>
          <Button size="sm" variant="outline" onClick={addBenefit} data-testid="btn-add-benefit">
            <Plus className="w-4 h-4 mr-1" /> Adicionar
          </Button>
        </CardHeader>
        <CardContent className="space-y-3">
          {(ms.benefits || []).map((b, i) => (
            <div key={i} className="flex gap-2 items-start p-3 border rounded-lg" data-testid={`benefit-item-${i}`}>
              <div className="flex-1 space-y-2">
                <Input placeholder="Título" value={b.title || ''} onChange={(e) => updateBenefit(i, { title: e.target.value })} />
                <Textarea rows={2} placeholder="Descrição" value={b.description || ''} onChange={(e) => updateBenefit(i, { description: e.target.value })} />
              </div>
              <Button size="sm" variant="ghost" onClick={() => removeBenefit(i)} className="text-rose-600" data-testid={`btn-remove-benefit-${i}`}>
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          ))}
          {(ms.benefits || []).length === 0 && <p className="text-sm text-slate-500 text-center py-4">Nenhum benefício. Clique em Adicionar.</p>}
        </CardContent>
      </Card>

      {/* Depoimentos */}
      <Card className="mb-6">
        <CardHeader className="flex-row items-center justify-between space-y-0">
          <CardTitle>Depoimentos ({(ms.testimonials || []).length})</CardTitle>
          <Button size="sm" variant="outline" onClick={addTestimonial} data-testid="btn-add-testimonial">
            <Plus className="w-4 h-4 mr-1" /> Adicionar
          </Button>
        </CardHeader>
        <CardContent className="space-y-3">
          {(ms.testimonials || []).map((t, i) => (
            <div key={i} className="flex gap-2 items-start p-3 border rounded-lg" data-testid={`testimonial-item-${i}`}>
              <div className="flex-1 space-y-2">
                <div className="grid grid-cols-3 gap-2">
                  <Input placeholder="Nome" value={t.name || ''} onChange={(e) => updateTestimonial(i, { name: e.target.value })} />
                  <Input placeholder="Comércio" value={t.business || ''} onChange={(e) => updateTestimonial(i, { business: e.target.value })} />
                  <Input placeholder="Cidade" value={t.city || ''} onChange={(e) => updateTestimonial(i, { city: e.target.value })} />
                </div>
                <Textarea rows={2} placeholder="Depoimento" value={t.text || ''} onChange={(e) => updateTestimonial(i, { text: e.target.value })} />
                <ImageUploadButton value={t.logo_url} onChange={(url) => updateTestimonial(i, { logo_url: url })} pasta="parceiros" label="logo do cliente" testId={`partner-testimonial-logo-${i}`} />
              </div>
              <Button size="sm" variant="ghost" onClick={() => removeTestimonial(i)} className="text-rose-600" data-testid={`btn-remove-testimonial-${i}`}>
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          ))}
          {(ms.testimonials || []).length === 0 && <p className="text-sm text-slate-500 text-center py-4">Nenhum depoimento cadastrado.</p>}
        </CardContent>
      </Card>

      <div className="flex justify-end sticky bottom-4 z-10">
        <Button onClick={salvar} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 shadow-xl h-12 px-8" data-testid="btn-save-minisite-bottom">
          <Save className="w-4 h-4 mr-2" />
          {saving ? 'Salvando...' : 'Salvar Minisite'}
        </Button>
      </div>
    </div>
  );
};

export default PartnerMinisiteEditorPage;
